import SubmitAccentButton from '@/components/submit-accent-button/submit-accent-button';
import validationService from '@/services/validation.service';
import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import { Button, Divider, InlineAlert, Input, Modal, Text } from '@cmpsr/components';
import { OnDone } from '../../edit-email-address.controller';
import useModalUpdateEmailController from './modal-update-email.controller';

type UpdateEmailModalProps = {
  isOpen: boolean;
  onCloseClick: () => void;
  onDone: OnDone<string>;
};

export default function ModalUpdateEmail({ isOpen, onCloseClick, onDone }: UpdateEmailModalProps) {
  const { form, validateEmailsMatch, handleSubmit } = useModalUpdateEmailController(onDone);

  return (
    <Modal isOpen={isOpen} onClose={onCloseClick} isCentered>
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        bg="background-page"
        borderRadius="radii-2"
        width={{ base: '343px', lg: '408px' }}
        p={{ base: 'spacer-5', lg: 'spacer-10' }}
        gap="spacer-6"
      >
        <Modal.Header bg="background-page" p={0}>
          <Text variant="text-header-2XL" mb="spacer-2">
            Update{' '}
            <Text as="span" textStyle="text-header-2xl-v">
              email
            </Text>
          </Text>
          <Text variant="text-body-regular" color="text-secondary">
            We'll send you an email to confirm your new email address.
          </Text>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body bg="background-page" display="flex" flexDirection="column" gap="spacer-6" p={0}>
            <Divider />
            <FormControl
              id="updateEmail"
              isInvalid={!!(form.formState.errors.newEmail || form.formState.errors.confirmNewEmail)}
            >
              <FormLabel htmlFor="newEmail">
                <Text variant="text-body-floating-label-regular" textTransform="uppercase">
                  new email
                </Text>
              </FormLabel>
              <Input
                id="newEmail"
                type="newEmail"
                aria-label="New Email"
                variant="outline"
                size="l"
                placeholder="Enter new email address"
                isInvalid={!!(form.formState.errors.newEmail || form.formState.errors.confirmNewEmail)}
                isDisabled={form.formState.isSubmitting}
                {...form.register('newEmail', {
                  required: 'Please type an email address',
                  validate: {
                    validateEmail: (v: string) => {
                      const isValid = validationService.validateEmail(v);
                      return isValid || 'Please provide a valid email address';
                    },
                  },
                })}
              />
              <FormLabel htmlFor="confirmNewEmail" mt="spacer-6">
                <Text variant="text-body-floating-label-regular" textTransform="uppercase">
                  confirm new email
                </Text>
              </FormLabel>
              <Input
                id="confirmNewEmail"
                type="confirmNewEmail"
                aria-label="Confirm New Email"
                variant="outline"
                size="l"
                placeholder="Confirm new email address"
                isInvalid={!!(form.formState.errors.confirmNewEmail || form.formState.errors.newEmail)}
                {...form.register('confirmNewEmail', {
                  required: 'Please type an email address',
                  validate: {
                    validateEmail: (v: string) => {
                      const isValid = validationService.validateEmail(v);
                      return isValid || 'Please provide a valid email address';
                    },
                    match: validateEmailsMatch,
                  },
                })}
              />
              {form.formState.errors.newEmail?.message || form.formState.errors.confirmNewEmail?.message ? (
                <FormErrorMessage>
                  <InlineAlert status="error" mt="spacer-6" mb="spacer-6" alignItems="flex-start">
                    <InlineAlert.Icon />
                    <InlineAlert.Message>
                      {form.formState.errors.newEmail?.message || form.formState.errors.confirmNewEmail?.message}
                    </InlineAlert.Message>
                  </InlineAlert>
                </FormErrorMessage>
              ) : null}
            </FormControl>
          </Modal.Body>
          <Divider mb="spacer-6" />
          <Modal.Footer bg="background-page" flexDirection="column" gap="spacer-3" p={0}>
            <SubmitAccentButton
              size={{ base: 'm', lg: 'l' }}
              isDisabled={!!(form.formState.errors.newEmail && form.formState.errors.confirmNewEmail)}
              isLoading={form.formState.isSubmitting}
            >
              Update Email address
            </SubmitAccentButton>
            <Button variant="primary-alt" size={{ base: 'm', lg: 'l' }} width="100%" onClick={onCloseClick}>
              Cancel
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Content>
    </Modal>
  );
}
